import { BreakpointObserver } from '@angular/cdk/layout';
import { TitleCasePipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { mfaMailIcon, mfaPhoneIcon, mfaTextIcon } from '@ld.consumer-portal/data-uri';
import { PlatformService, ThemeService } from '@ld.consumer-portal/utils';
import { map } from 'rxjs';

export type MfaMethodType = 'email' | 'text' | 'phone' | 'call';

export interface MfaOption {
  key?: string;
  value: string;
  label?: string;
  masked?: string;
}

export interface MfaVerificationRequest {
  method: MfaMethodType;
  value: string;
}

@Component({
  selector: 'ld-mfa-card',
  imports: [
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    MatTooltipModule,
    RouterLink,
    TitleCasePipe,
    TranslocoDirective,
    UpperCasePipe,
  ],
  templateUrl: './mfa-card.component.html',
  styleUrl: './mfa-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MfaCardComponent {
  readonly mailIcon = mfaMailIcon;
  readonly phoneIcon = mfaPhoneIcon;
  readonly textIcon = mfaTextIcon;

  private readonly transloco = inject(TranslocoService);
  private readonly platformService = inject(PlatformService);
  private readonly breakpointObserver = inject(BreakpointObserver);

  private readonly isNativeMobile = this.platformService.isNativeMobile;

  private readonly isMobileViewport = toSignal(
    this.breakpointObserver.observe('(max-width: 767px)').pipe(map((result) => result.matches)),
    { initialValue: this.breakpointObserver.isMatched('(max-width: 767px)') },
  );

  readonly isMobile = computed(() => this.isNativeMobile() || this.isMobileViewport());

  readonly showMfaInfoSheet = signal(false);

  embedded = input<boolean>(false);

  showLogo = input<boolean>(true);

  showLockIcon = input<boolean>(false);

  leftAlign = input<boolean>(false);

  showTitle = input<boolean>(true);

  title = input<string>(this.transloco.translate('mfaVerificationTitle'));

  description = input<string>(this.transloco.translate('mfaVerificationDescription'));

  showFaqLink = input<boolean>(false);

  hideEmailOption = input<boolean>(false);

  accountId = input<string>('');

  protected readonly faqLink = computed(() => {
    const id = this.accountId();
    return id ? `/help/${id}/faqs` : '/dashboard';
  });

  emailOptions = input<MfaOption[]>([]);

  textPhoneOptions = input<MfaOption[]>([]);

  callPhoneOptions = input<MfaOption[]>([]);

  initialEmailValue = input<string | null>(null);

  initialPhoneValue = input<string | null>(null);

  initialCallPhoneValue = input<string | null>(null);

  showMfaInfoIcon = input<boolean>(true);

  mfaInfoTooltip = input<string>(this.transloco.translate('mfaWhatIsCodeTooltip'));

  emailOnlyDescription = input<string | null>(null);

  emailVerification = output<MfaVerificationRequest>();

  textVerification = output<MfaVerificationRequest>();

  callVerification = output<MfaVerificationRequest>();

  cancel = output<void>();

  private readonly themeService = inject(ThemeService);

  readonly logo = this.themeService.getLogoUrl();

  readonly logoAlt = this.themeService.getPartnerDisplayName();

  emailLoading = signal(false);
  textLoading = signal(false);
  callLoading = signal(false);

  protected readonly anyLoading = computed(() => this.emailLoading() || this.textLoading() || this.callLoading());

  selectedEmail = signal<MfaOption | null>(null);
  selectedPhone = signal<MfaOption | null>(null);
  selectedCallPhone = signal<MfaOption | null>(null);

  protected readonly initialEmail = computed(() =>
    this.resolveInitialOption(this.emailOptions(), this.initialEmailValue()),
  );
  protected readonly initialPhone = computed(() =>
    this.resolveInitialOption(this.textPhoneOptions(), this.initialPhoneValue()),
  );
  protected readonly initialCallPhone = computed(() =>
    this.resolveInitialOption(this.callPhoneOptions(), this.initialCallPhoneValue()),
  );

  private resolveInitialOption(options: MfaOption[], preferredValue: string | null): MfaOption | null {
    if (preferredValue) {
      const match = options.find((option) => option.value === preferredValue);
      if (match) {
        return match;
      }
    }
    return options[0] ?? null;
  }

  protected readonly displayDescription = computed(() => {
    if (
      this.emailOnlyDescription() &&
      this.emailOptions().length > 0 &&
      this.textPhoneOptions().length === 0 &&
      this.callPhoneOptions().length === 0
    ) {
      return this.emailOnlyDescription();
    }
    return this.description();
  });

  onSelectEmail(option: MfaOption): void {
    if (this.anyLoading()) return;
    this.emailLoading.set(true);
    this.emailVerification.emit({
      method: 'email',
      value: option.value,
    });
  }

  onSelectTextPhone(option: MfaOption): void {
    if (this.anyLoading()) return;
    this.textLoading.set(true);
    this.textVerification.emit({
      method: 'text',
      value: option.value,
    });
  }

  onSelectCallPhone(option: MfaOption): void {
    if (this.anyLoading()) return;
    this.callLoading.set(true);
    this.callVerification.emit({
      method: 'call',
      value: option.value,
    });
  }

  onCancel(): void {
    this.cancel.emit();
  }

  openMfaInfoSheet(): void {
    this.showMfaInfoSheet.set(true);
  }

  closeMfaInfoSheet(): void {
    this.showMfaInfoSheet.set(false);
  }

  setLoading(method: MfaMethodType, loading: boolean): void {
    switch (method) {
      case 'email':
        this.emailLoading.set(loading);
        break;
      case 'text':
        this.textLoading.set(loading);
        break;
      case 'call':
        this.callLoading.set(loading);
        break;
    }
  }

  resetLoading(): void {
    this.emailLoading.set(false);
    this.textLoading.set(false);
    this.callLoading.set(false);
  }
}
